export const calculusIICourse = {
  id: 'calculus-ii',
  title: 'Calculus II',
  description: 'Advanced calculus covering integration techniques, infinite series, parametric equations, polar coordinates, and multivariable calculus foundations with applications to computer science and engineering.',
  credits: 4,
  duration: '15 weeks',
  level: 'Intermediate',
  category: 'Coding',
  learningObjectives: [
    'Master various integration techniques including substitution, integration by parts, and partial fractions',
    'Understand and apply concepts of infinite sequences and series, including convergence tests',
    'Work with parametric equations and polar coordinates in mathematical modeling',
    'Explore improper integrals and their applications in probability and engineering',
    'Apply calculus concepts to solve real-world problems in computer science and physics',
    'Develop computational skills using mathematical software and programming',
    'Understand the theoretical foundations leading to multivariable calculus',
    'Build mathematical reasoning and proof-writing skills'
  ],
  prerequisites: ['Calculus I', 'Strong algebraic manipulation skills'],
  skills: [
    'Advanced Integration Techniques',
    'Series and Convergence Analysis',
    'Parametric and Polar Mathematics',
    'Mathematical Modeling',
    'Computational Mathematics',
    'Problem Solving',
    'Mathematical Software (MATLAB/Python)',
    'Proof Writing',
    'Error Analysis',
    'Numerical Methods',
    'Mathematical Communication',
    'Applied Mathematics'
  ],
  marketDemand: 'High',
  averageSalary: '$70,000 - $130,000',
  careerPaths: [
    'Data Scientist',
    'Machine Learning Engineer',
    'Software Engineer',
    'Quantitative Analyst',
    'Research Scientist',
    'Engineering Roles',
    'Actuarial Science',
    'Physics/Mathematics Graduate Studies',
    'Computer Graphics Developer',
    'Financial Modeling Specialist',
    'Operations Research Analyst',
    'Algorithm Developer'
  ],
  industryApplications: [
    'Machine Learning and AI',
    'Computer Graphics and Animation',
    'Financial Modeling',
    'Engineering Design',
    'Scientific Computing',
    'Data Analysis and Statistics',
    'Signal Processing',
    'Game Development',
    'Optimization Problems',
    'Physics Simulations',
    'Economic Modeling',
    'Biostatistics'
  ],
  modules: [
    {
      id: 'integration-techniques',
      title: 'Advanced Integration Techniques',
      duration: '3 weeks',
      learningObjectives: [
        'Master integration by substitution and trigonometric substitution',
        'Apply integration by parts effectively',
        'Decompose rational functions using partial fractions',
        'Solve integrals involving trigonometric functions'
      ],
      content: [
        {
          type: 'lesson',
          title: 'Integration by Substitution',
          content: `# Integration by Substitution

## Introduction

Integration by substitution is one of the most powerful techniques for evaluating integrals. It's based on the chain rule for differentiation and allows us to transform complex integrals into simpler ones.

## The Substitution Method

The basic idea is to substitute u for a function of x, where du = f'(x)dx.

### Basic Substitution Formula
If we have ∫ f(g(x)) · g'(x) dx, we can substitute:
- u = g(x)
- du = g'(x) dx

Then the integral becomes ∫ f(u) du

## Examples

### Example 1: Simple Substitution
Evaluate ∫ 2x(x² + 1)³ dx

**Solution:**
Let u = x² + 1
Then du = 2x dx

The integral becomes:
∫ u³ du = u⁴/4 + C = (x² + 1)⁴/4 + C

### Example 2: Trigonometric Substitution
Evaluate ∫ cos(x) sin²(x) dx

**Solution:**
Let u = sin(x)
Then du = cos(x) dx

The integral becomes:
∫ u² du = u³/3 + C = sin³(x)/3 + C

## Trigonometric Substitution

For integrals involving √(a² - x²), √(a² + x²), or √(x² - a²), we use trigonometric substitutions:

### Type 1: √(a² - x²)
Substitute x = a sin(θ), dx = a cos(θ) dθ
Then √(a² - x²) = a cos(θ)

### Type 2: √(a² + x²)
Substitute x = a tan(θ), dx = a sec²(θ) dθ
Then √(a² + x²) = a sec(θ)

### Type 3: √(x² - a²)
Substitute x = a sec(θ), dx = a sec(θ) tan(θ) dθ
Then √(x² - a²) = a tan(θ)

## Programming Implementation

Here's how we can implement these techniques computationally:

\`\`\`python
import sympy as sp
import numpy as np

# Define symbolic variables
x, u = sp.symbols('x u')

# Example: Substitution method
def integrate_by_substitution(expr, var, substitution):
    """
    Perform integration by substitution
    """
    # Substitute u for the given expression
    substituted = expr.subs(substitution)
    
    # Integrate with respect to u
    result = sp.integrate(substituted, u)
    
    # Substitute back
    return result.subs(u, substitution)

# Example usage
expr = 2*x*(x**2 + 1)**3
substitution = x**2 + 1
result = integrate_by_substitution(expr, x, substitution)
print(f"∫ 2x(x² + 1)³ dx = {result}")
\`\`\`

## Practice Problems

1. ∫ x√(x² + 4) dx
2. ∫ e^(2x) / (1 + e^(2x)) dx
3. ∫ sin(ln(x)) / x dx
4. ∫ x² / √(9 - x²) dx

## Applications in Computer Science

Integration by substitution is fundamental in:
- Probability density function normalizations
- Signal processing transformations
- Computer graphics lighting calculations
- Machine learning optimization algorithms`
        },
        {
          type: 'lesson',
          title: 'Integration by Parts',
          content: `# Integration by Parts

## The Integration by Parts Formula

Integration by parts is based on the product rule for differentiation:

**Formula:** ∫ u dv = uv - ∫ v du

Where:
- u and dv are chosen from the integrand
- du = u' dx
- v = ∫ dv

## LIATE Rule for Choosing u

When selecting u and dv, use the LIATE priority:
1. **L**ogarithmic functions (ln x, log x)
2. **I**nverse trigonometric functions (arcsin x, arctan x)
3. **A**lgebraic functions (x, x², etc.)
4. **T**rigonometric functions (sin x, cos x)
5. **E**xponential functions (eˣ, 2ˣ)

Choose u as the function highest on this list.

## Step-by-Step Process

1. Identify u and dv using LIATE
2. Find du by differentiating u
3. Find v by integrating dv
4. Apply the formula: ∫ u dv = uv - ∫ v du
5. Evaluate the remaining integral ∫ v du

## Examples

### Example 1: ∫ x eˣ dx
Using LIATE:
- u = x (algebraic)
- dv = eˣ dx (exponential)

Then:
- du = dx
- v = eˣ

Applying the formula:
∫ x eˣ dx = x·eˣ - ∫ eˣ dx = x·eˣ - eˣ + C = eˣ(x - 1) + C

### Example 2: ∫ ln(x) dx
Using LIATE:
- u = ln(x) (logarithmic)
- dv = dx (constant)

Then:
- du = (1/x) dx
- v = x

Applying the formula:
∫ ln(x) dx = x·ln(x) - ∫ x·(1/x) dx = x·ln(x) - ∫ 1 dx = x·ln(x) - x + C

### Example 3: Repeated Integration by Parts
∫ x² eˣ dx

First application:
- u = x², dv = eˣ dx
- du = 2x dx, v = eˣ

∫ x² eˣ dx = x²eˣ - ∫ 2x eˣ dx

Second application on ∫ 2x eˣ dx:
- u = 2x, dv = eˣ dx  
- du = 2 dx, v = eˣ

∫ 2x eˣ dx = 2x eˣ - ∫ 2 eˣ dx = 2x eˣ - 2eˣ

Final result:
∫ x² eˣ dx = x²eˣ - (2x eˣ - 2eˣ) + C = eˣ(x² - 2x + 2) + C

## Special Cases

### Definite Integrals
For definite integrals, apply limits to the uv term:
∫[a,b] u dv = [uv]ᵇₐ - ∫[a,b] v du

### Reduction Formulas
Some integrals require reduction formulas, like:
∫ sinⁿ(x) dx where n is a positive integer

## Programming Implementation

\`\`\`python
import sympy as sp

def integration_by_parts(u_func, dv_func, var):
    """
    Perform integration by parts
    """
    # Calculate du and v
    du = sp.diff(u_func, var)
    v = sp.integrate(dv_func, var)
    
    # Apply the formula: ∫u dv = uv - ∫v du
    uv_term = u_func * v
    integral_vdu = sp.integrate(v * du, var)
    
    return uv_term - integral_vdu

# Example usage
x = sp.Symbol('x')
u = x
dv = sp.exp(x)

result = integration_by_parts(u, dv, x)
print(f"∫ x e^x dx = {result}")
\`\`\`

## Applications

Integration by parts is essential in:
- Signal processing (Fourier transforms)
- Probability theory (moment generating functions)
- Physics (energy calculations)
- Economics (consumer surplus calculations)
- Machine learning (regularization techniques)`
        },
        {
          type: 'interactive_exercise',
          title: 'Master Integration Techniques',
          component: 'InteractiveExercise',
          exerciseData: {
            instructions: `Create a JavaScript function that determines the best integration technique for different types of integrals and implements basic substitution.

Your function should:
1. Take an integral expression as input (as a string description)
2. Determine if substitution or integration by parts is more appropriate
3. For substitution integrals, identify the u-substitution
4. Return the technique and substitution (if applicable)

Test cases:
- "2x(x^2 + 1)^3" → should suggest u-substitution with u = x^2 + 1
- "x*e^x" → should suggest integration by parts
- "cos(x)*sin^2(x)" → should suggest u-substitution with u = sin(x)

Implement the analysis logic and test with the given examples.`,
            initialCode: `// Create an integration technique analyzer
class IntegrationAnalyzer {
  constructor() {
    this.techniques = {
      SUBSTITUTION: 'substitution',
      INTEGRATION_BY_PARTS: 'integration_by_parts'
    };
  }
  
  // Analyze the integral and suggest technique
  analyzeIntegral(expression) {
    // Implement analysis logic here
    // Return: { technique: string, substitution?: string, explanation: string }
  }
  
  // Check if expression fits u-substitution pattern
  checkSubstitution(expression) {
    // Look for patterns like f(g(x)) * g'(x)
    // Return substitution suggestion
  }
  
  // Check if expression fits integration by parts pattern
  checkIntegrationByParts(expression) {
    // Look for products of different function types
    // Apply LIATE rule
  }
}

// Test your implementation
const analyzer = new IntegrationAnalyzer();

console.log(analyzer.analyzeIntegral("2x(x^2 + 1)^3"));
console.log(analyzer.analyzeIntegral("x*e^x"));
console.log(analyzer.analyzeIntegral("cos(x)*sin^2(x)"));`,
            expectedOutput: `{ technique: 'substitution', substitution: 'u = x^2 + 1', explanation: 'Pattern f(g(x)) * g\'(x) detected' }
{ technique: 'integration_by_parts', explanation: 'Product of algebraic and exponential functions (LIATE)' }
{ technique: 'substitution', substitution: 'u = sin(x)', explanation: 'Trigonometric composition pattern detected' }`,
            hints: [
              'Use regular expressions to identify patterns in the expression',
              'For substitution, look for inner function and its derivative',
              'For integration by parts, identify function types using LIATE',
              'Consider common patterns like polynomial*exponential, trigonometric compositions',
              'Return objects with technique, substitution, and explanation properties'
            ]
          }
        }
      ],
      assessments: [
        {
          type: 'Integration Techniques Test',
          title: 'Comprehensive Integration Methods Assessment',
          points: 100,
          dueWeek: 3
        }
      ]
    },
    {
      id: 'sequences-series',
      title: 'Sequences and Infinite Series',
      duration: '4 weeks',
      learningObjectives: [
        'Understand the concepts of sequences and their limits',
        'Analyze convergence and divergence of infinite series',
        'Apply various convergence tests effectively',
        'Work with power series and Taylor series'
      ],
      content: [
        {
          type: 'lesson',
          title: 'Sequences and Limits',
          content: `# Sequences and Their Limits

## Definition of a Sequence

A sequence is a function whose domain is the set of positive integers. We typically write a sequence as:
{aₙ} = {a₁, a₂, a₃, ..., aₙ, ...}

Examples:
- {1, 1/2, 1/3, 1/4, ...} → aₙ = 1/n
- {1, 4, 9, 16, ...} → aₙ = n²
- {1, -1, 1, -1, ...} → aₙ = (-1)ⁿ⁺¹

## Limit of a Sequence

A sequence {aₙ} has limit L if for every ε > 0, there exists N such that |aₙ - L| < ε for all n > N.

We write: lim(n→∞) aₙ = L

## Types of Sequences

### Convergent Sequences
A sequence converges if it has a finite limit.

Example: aₙ = 1/n converges to 0

### Divergent Sequences
A sequence diverges if:
1. It approaches ±∞
2. It oscillates without approaching a single value

Examples:
- aₙ = n² diverges to +∞
- aₙ = (-1)ⁿ oscillates between -1 and 1

## Properties of Limits

If lim(n→∞) aₙ = A and lim(n→∞) bₙ = B, then:

1. lim(n→∞) (aₙ + bₙ) = A + B
2. lim(n→∞) (aₙ · bₙ) = A · B
3. lim(n→∞) (aₙ / bₙ) = A / B (if B ≠ 0)
4. lim(n→∞) c·aₙ = c·A (for constant c)

## Important Limit Theorems

### Squeeze Theorem for Sequences
If aₙ ≤ bₙ ≤ cₙ and lim(n→∞) aₙ = lim(n→∞) cₙ = L, then lim(n→∞) bₙ = L

### Growth Rates
For large n:
- Exponential grows faster than polynomial: lim(n→∞) nᵏ/aⁿ = 0 (a > 1)
- Polynomial grows faster than logarithmic: lim(n→∞) (ln n)/nᵏ = 0 (k > 0)

## Recursive Sequences

Some sequences are defined recursively:
- a₁ = initial value
- aₙ₊₁ = f(aₙ) for some function f

Example: Fibonacci sequence
- a₁ = 1, a₂ = 1
- aₙ₊₁ = aₙ + aₙ₋₁

## Computational Approach

\`\`\`python
import numpy as np
import matplotlib.pyplot as plt

def sequence_limit_analysis(sequence_func, n_terms=1000):
    """
    Analyze the behavior of a sequence
    """
    n_values = np.arange(1, n_terms + 1)
    sequence_values = [sequence_func(n) for n in n_values]
    
    # Plot the sequence
    plt.figure(figsize=(10, 6))
    plt.plot(n_values, sequence_values, 'b.', markersize=2)
    plt.xlabel('n')
    plt.ylabel('aₙ')
    plt.title('Sequence Behavior')
    plt.grid(True)
    
    # Estimate limit (if it exists)
    last_values = sequence_values[-100:]  # Last 100 terms
    if max(last_values) - min(last_values) < 0.001:
        estimated_limit = np.mean(last_values)
        print(f"Sequence appears to converge to: {estimated_limit:.6f}")
    else:
        print("Sequence appears to diverge or oscillate")
    
    return sequence_values

# Example usage
def harmonic_sequence(n):
    return 1/n

sequence_limit_analysis(harmonic_sequence)
\`\`\`

## Applications in Computer Science

Sequences appear in:
- Algorithm analysis (time complexity)
- Numerical methods (convergence of iterations)
- Machine learning (convergence of optimization algorithms)
- Computer graphics (recursive subdivision)
- Data structures (performance analysis)`
        },
        {
          type: 'lesson',
          title: 'Infinite Series and Convergence Tests',
          content: `# Infinite Series and Convergence Tests

## Definition of an Infinite Series

An infinite series is the sum of the terms of an infinite sequence:
∑(n=1 to ∞) aₙ = a₁ + a₂ + a₃ + ... + aₙ + ...

## Partial Sums

The nth partial sum is: Sₙ = ∑(k=1 to n) aₖ

A series converges if lim(n→∞) Sₙ exists and is finite.

## Basic Convergence Tests

### 1. Divergence Test (nth Term Test)
If lim(n→∞) aₙ ≠ 0, then ∑aₙ diverges.
Note: If lim(n→∞) aₙ = 0, the test is inconclusive.

### 2. Integral Test
For positive, decreasing functions f(x):
∑(n=1 to ∞) f(n) and ∫(1 to ∞) f(x)dx have the same convergence behavior.

Example: Harmonic series ∑(1/n)
∫(1 to ∞) (1/x)dx = [ln x](1 to ∞) = ∞
Therefore, ∑(1/n) diverges.

### 3. Comparison Test
Given series ∑aₙ and ∑bₙ where 0 ≤ aₙ ≤ bₙ:
- If ∑bₙ converges, then ∑aₙ converges
- If ∑aₙ diverges, then ∑bₙ diverges

### 4. Limit Comparison Test
For positive series ∑aₙ and ∑bₙ:
If lim(n→∞) (aₙ/bₙ) = L > 0, then both series have the same convergence behavior.

### 5. Ratio Test
For series ∑aₙ, let L = lim(n→∞) |aₙ₊₁/aₙ|
- If L < 1, the series converges absolutely
- If L > 1, the series diverges  
- If L = 1, the test is inconclusive

### 6. Root Test
For series ∑aₙ, let L = lim(n→∞) ⁿ√|aₙ|
- If L < 1, the series converges absolutely
- If L > 1, the series diverges
- If L = 1, the test is inconclusive

## Important Series

### Geometric Series
∑(r^n) = 1/(1-r) if |r| < 1, diverges if |r| ≥ 1

### p-Series
∑(1/n^p) converges if p > 1, diverges if p ≤ 1

### Exponential Series
∑(x^n/n!) = e^x (converges for all x)

## Alternating Series

An alternating series has the form ∑(-1)ⁿ aₙ or ∑(-1)ⁿ⁺¹ aₙ

### Alternating Series Test
If aₙ > 0, aₙ is decreasing, and lim(n→∞) aₙ = 0, then ∑(-1)ⁿ aₙ converges.

### Error Estimation
For a convergent alternating series, the error in using Sₙ to approximate the sum is at most |aₙ₊₁|.

## Absolute vs Conditional Convergence

- **Absolutely convergent**: ∑|aₙ| converges
- **Conditionally convergent**: ∑aₙ converges but ∑|aₙ| diverges

If a series is absolutely convergent, it is also convergent.

## Computational Implementation

\`\`\`python
import numpy as np

class SeriesAnalyzer:
    def __init__(self):
        pass
    
    def ratio_test(self, series_func, n_terms=1000):
        """Apply the ratio test to a series"""
        ratios = []
        for n in range(1, n_terms):
            an = abs(series_func(n))
            an_plus_1 = abs(series_func(n + 1))
            if an != 0:
                ratios.append(an_plus_1 / an)
        
        if ratios:
            limit = np.mean(ratios[-100:])  # Estimate limit
            print(f"Ratio test limit ≈ {limit:.6f}")
            if limit < 1:
                return "Converges absolutely"
            elif limit > 1:
                return "Diverges"
            else:
                return "Inconclusive"
        return "Cannot compute ratio"
    
    def partial_sum_convergence(self, series_func, n_terms=10000):
        """Check convergence by examining partial sums"""
        partial_sums = []
        current_sum = 0
        
        for n in range(1, n_terms + 1):
            current_sum += series_func(n)
            partial_sums.append(current_sum)
        
        # Check if partial sums are stabilizing
        last_100 = partial_sums[-100:]
        if abs(max(last_100) - min(last_100)) < 0.001:
            print(f"Series appears to converge to: {partial_sums[-1]:.6f}")
            return True
        else:
            print("Series appears to diverge")
            return False

# Example: Analyze geometric series with r = 0.5
def geometric_series(n):
    return (0.5) ** n

analyzer = SeriesAnalyzer()
print(analyzer.ratio_test(geometric_series))
analyzer.partial_sum_convergence(geometric_series)
\`\`\`

## Applications

Series convergence is crucial in:
- Numerical analysis (iterative methods)
- Signal processing (Fourier series)
- Machine learning (infinite neural networks)
- Computer graphics (fractal generation)
- Probability theory (generating functions)`
        },
        {
          type: 'interactive_exercise',
          title: 'Series Convergence Analysis Tool',
          component: 'InteractiveExercise',
          exerciseData: {
            instructions: `Build a comprehensive series convergence analyzer that can:

1. Apply multiple convergence tests to a given series
2. Determine which test is most appropriate for each series type
3. Estimate the sum for convergent series
4. Provide explanations for the convergence/divergence conclusion

Your analyzer should handle:
- Geometric series: a*r^n
- p-series: 1/n^p  
- Exponential series: a^n/n!
- Alternating series: (-1)^n * a_n

Implement methods for:
- ratioTest(series)
- integralTest(series) 
- comparisonTest(series, knownSeries)
- estimateSum(series, terms)

Test with: geometric series r=0.3, p-series p=2, alternating harmonic series`,
            initialCode: `class SeriesConvergenceAnalyzer {
  constructor() {
    // Initialize with known convergent/divergent series for comparison
    this.knownSeries = {
      geometric: (r) => Math.abs(r) < 1,
      pSeries: (p) => p > 1,
      harmonic: false // diverges
    };
  }
  
  // Determine series type from mathematical expression
  classifySeries(expression) {
    // Analyze the expression to determine series type
    // Return: { type: string, parameters: object }
  }
  
  // Apply ratio test: lim |a_{n+1}/a_n|
  ratioTest(seriesFunc, maxTerms = 1000) {
    // Calculate the ratio test limit
    // Return: { converges: boolean, limit: number, explanation: string }
  }
  
  // Apply root test: lim (|a_n|)^(1/n)
  rootTest(seriesFunc, maxTerms = 1000) {
    // Calculate the root test limit
    // Return convergence information
  }
  
  // Estimate sum for convergent series
  estimateSum(seriesFunc, terms = 10000) {
    // Calculate partial sums and estimate total
    // Return: { sum: number, confidence: string }
  }
  
  // Comprehensive analysis using multiple tests
  analyze(expression) {
    // Apply all relevant tests and provide conclusion
    // Return complete analysis
  }
}

// Test your implementation
const analyzer = new SeriesConvergenceAnalyzer();

// Test cases
console.log("Geometric series (r=0.3):");
console.log(analyzer.analyze("0.3^n"));

console.log("\\np-series (p=2):");
console.log(analyzer.analyze("1/n^2"));

console.log("\\nAlternating harmonic:");
console.log(analyzer.analyze("(-1)^n/n"));`,
            expectedOutput: `Geometric series (r=0.3):
{ type: 'geometric', converges: true, sum: 1.428571, tests: ['ratio'], explanation: 'Geometric series with |r| = 0.3 < 1' }

p-series (p=2):
{ type: 'p-series', converges: true, sum: 1.644934, tests: ['integral'], explanation: 'p-series with p = 2 > 1' }

Alternating harmonic:
{ type: 'alternating', converges: true, sum: 0.693147, tests: ['alternating'], explanation: 'Alternating series test satisfied' }`,
            hints: [
              'Use regular expressions to parse mathematical expressions',
              'Implement numerical approximation for ratio and root tests',
              'For geometric series: sum = a/(1-r) when |r| < 1',
              'For p-series with p=2: sum approaches π²/6 ≈ 1.645',
              'Alternating harmonic series converges to ln(2) ≈ 0.693',
              'Return objects with convergence info, estimated sums, and explanations'
            ]
          }
        }
      ],
      assessments: [
        {
          type: 'Series Analysis Project',
          title: 'Convergence Testing and Applications',
          points: 125,
          dueWeek: 7
        }
      ]
    },
    {
      id: 'parametric-polar',
      title: 'Parametric Equations and Polar Coordinates',
      duration: '3 weeks',
      learningObjectives: [
        'Work with parametric equations and their derivatives',
        'Convert between Cartesian and polar coordinate systems',
        'Calculate areas and arc lengths using parametric and polar forms',
        'Apply these concepts to real-world modeling problems'
      ],
      content: [
        {
          type: 'lesson',
          title: 'Parametric Equations',
          content: `# Parametric Equations

## Introduction

Parametric equations express the coordinates of points on a curve as functions of a parameter, typically t:
- x = f(t)
- y = g(t)

This representation is particularly useful for curves that cannot be expressed as functions y = f(x).

## Examples of Parametric Curves

### Circle
x = r cos(t), y = r sin(t)
where 0 ≤ t ≤ 2π

### Ellipse
x = a cos(t), y = b sin(t)
where 0 ≤ t ≤ 2π

### Line
x = x₀ + at, y = y₀ + bt
represents a line passing through (x₀, y₀) with direction vector (a, b)

### Cycloid
x = r(t - sin(t)), y = r(1 - cos(t))
represents the curve traced by a point on a circle rolling along a line

## Calculus with Parametric Equations

### First Derivative
dy/dx = (dy/dt)/(dx/dt) = g'(t)/f'(t)

provided f'(t) ≠ 0

### Second Derivative
d²y/dx² = d/dx(dy/dx) = [d/dt(dy/dx)]/(dx/dt)

### Tangent Lines
At parameter value t = t₀, the slope of the tangent line is:
m = g'(t₀)/f'(t₀)

The tangent line is horizontal when g'(t) = 0 and f'(t) ≠ 0
The tangent line is vertical when f'(t) = 0 and g'(t) ≠ 0

## Arc Length

The arc length of a parametric curve from t = a to t = b is:
L = ∫[a,b] √[(dx/dt)² + (dy/dt)²] dt

## Area Under Parametric Curves

The area under a parametric curve from t = a to t = b is:
A = ∫[a,b] y(dx/dt) dt

## Programming Implementation

\`\`\`python
import numpy as np
import matplotlib.pyplot as plt
from scipy import integrate

class ParametricCurve:
    def __init__(self, x_func, y_func, dx_func, dy_func):
        self.x = x_func
        self.y = y_func
        self.dx = dx_func  # dx/dt
        self.dy = dy_func  # dy/dt
    
    def plot(self, t_start=0, t_end=2*np.pi, num_points=1000):
        """Plot the parametric curve"""
        t = np.linspace(t_start, t_end, num_points)
        x_vals = [self.x(t_val) for t_val in t]
        y_vals = [self.y(t_val) for t_val in t]
        
        plt.figure(figsize=(8, 8))
        plt.plot(x_vals, y_vals, 'b-', linewidth=2)
        plt.axis('equal')
        plt.grid(True)
        plt.xlabel('x')
        plt.ylabel('y')
        plt.title('Parametric Curve')
        plt.show()
    
    def derivative_at(self, t):
        """Calculate dy/dx at parameter t"""
        dx_dt = self.dx(t)
        dy_dt = self.dy(t)
        if abs(dx_dt) < 1e-10:
            return float('inf')  # Vertical tangent
        return dy_dt / dx_dt
    
    def arc_length(self, t_start, t_end):
        """Calculate arc length between t_start and t_end"""
        def speed(t):
            return np.sqrt(self.dx(t)**2 + self.dy(t)**2)
        
        result, _ = integrate.quad(speed, t_start, t_end)
        return result
    
    def area_under_curve(self, t_start, t_end):
        """Calculate area under the curve"""
        def integrand(t):
            return self.y(t) * self.dx(t)
        
        result, _ = integrate.quad(integrand, t_start, t_end)
        return result

# Example: Circle with radius 2
def x_circle(t):
    return 2 * np.cos(t)

def y_circle(t):
    return 2 * np.sin(t)

def dx_circle(t):
    return -2 * np.sin(t)

def dy_circle(t):
    return 2 * np.cos(t)

circle = ParametricCurve(x_circle, y_circle, dx_circle, dy_circle)
print(f"Arc length of full circle: {circle.arc_length(0, 2*np.pi):.6f}")
print(f"Expected: {4 * np.pi:.6f}")
\`\`\`

## Applications

Parametric equations are used in:
- Computer graphics (animation paths, bezier curves)
- Physics (projectile motion, orbital mechanics)
- Engineering (cam design, gear profiles)
- Robotics (path planning)
- Computer-aided design (CAD software)`
        },
        {
          type: 'lesson',
          title: 'Polar Coordinates',
          content: `# Polar Coordinates

## Introduction to Polar Coordinates

In polar coordinates, a point is described by:
- r: distance from the origin (radius)
- θ: angle from the positive x-axis (in radians)

Notation: (r, θ)

## Conversion Between Coordinate Systems

### Polar to Cartesian
- x = r cos(θ)
- y = r sin(θ)

### Cartesian to Polar
- r = √(x² + y²)
- θ = arctan(y/x) (with quadrant considerations)

## Common Polar Curves

### Circle centered at origin
r = a (constant radius)

### Circle passing through origin
r = a cos(θ) or r = a sin(θ)

### Rose curves
r = a cos(nθ) or r = a sin(nθ)
- n odd: n petals
- n even: 2n petals

### Limaçon
r = a + b cos(θ) or r = a + b sin(θ)
- If |a/b| < 1: inner loop
- If |a/b| = 1: cardioid
- If |a/b| > 1: no inner loop

### Lemniscate (Figure-8)
r² = a² cos(2θ) or r² = a² sin(2θ)

### Spiral of Archimedes
r = aθ

## Calculus in Polar Coordinates

### Area in Polar Coordinates
Area enclosed by r = f(θ) from θ = α to θ = β:
A = (1/2) ∫[α,β] r² dθ

### Arc Length in Polar Coordinates
L = ∫[α,β] √[r² + (dr/dθ)²] dθ

### Derivatives
For r = f(θ):
- dy/dx = (dr/dθ · sin(θ) + r · cos(θ))/(dr/dθ · cos(θ) - r · sin(θ))

## Programming Implementation

\`\`\`python
import numpy as np
import matplotlib.pyplot as plt

class PolarCurve:
    def __init__(self, r_func, dr_func=None):
        self.r = r_func
        self.dr = dr_func  # dr/dθ
    
    def plot(self, theta_start=0, theta_end=2*np.pi, num_points=1000):
        """Plot the polar curve"""
        theta = np.linspace(theta_start, theta_end, num_points)
        r_vals = [self.r(t) for t in theta]
        
        # Convert to Cartesian for plotting
        x_vals = [r * np.cos(t) for r, t in zip(r_vals, theta)]
        y_vals = [r * np.sin(t) for r, t in zip(r_vals, theta)]
        
        plt.figure(figsize=(8, 8))
        plt.plot(x_vals, y_vals, 'b-', linewidth=2)
        plt.axis('equal')
        plt.grid(True)
        plt.xlabel('x')
        plt.ylabel('y')
        plt.title('Polar Curve')
        plt.show()
    
    def area(self, theta_start, theta_end, num_points=10000):
        """Calculate area enclosed by the curve"""
        theta = np.linspace(theta_start, theta_end, num_points)
        r_squared = [(self.r(t))**2 for t in theta]
        
        # Numerical integration using trapezoidal rule
        dtheta = (theta_end - theta_start) / (num_points - 1)
        area = 0.5 * np.trapz(r_squared, dx=dtheta)
        return area
    
    def arc_length(self, theta_start, theta_end, num_points=10000):
        """Calculate arc length"""
        if self.dr is None:
            raise ValueError("dr/dθ function not provided")
        
        theta = np.linspace(theta_start, theta_end, num_points)
        integrand = []
        
        for t in theta:
            r_val = self.r(t)
            dr_val = self.dr(t)
            integrand.append(np.sqrt(r_val**2 + dr_val**2))
        
        dtheta = (theta_end - theta_start) / (num_points - 1)
        length = np.trapz(integrand, dx=dtheta)
        return length

# Example: Cardioid r = 1 + cos(θ)
def cardioid_r(theta):
    return 1 + np.cos(theta)

def cardioid_dr(theta):
    return -np.sin(theta)

cardioid = PolarCurve(cardioid_r, cardioid_dr)
print(f"Area of cardioid: {cardioid.area(0, 2*np.pi):.6f}")
print(f"Arc length of cardioid: {cardioid.arc_length(0, 2*np.pi):.6f}")

# Example: Rose curve r = cos(3θ)
def rose_r(theta):
    return np.abs(np.cos(3 * theta))  # Take absolute value for plotting

rose = PolarCurve(rose_r)
rose.plot(0, np.pi)  # Only need π for 3-petal rose
\`\`\`

## Applications

Polar coordinates are essential in:
- Engineering (radar systems, antenna patterns)
- Physics (orbital mechanics, wave propagation)
- Computer graphics (rotation transformations)
- Signal processing (frequency domain analysis)
- Navigation (GPS, compass bearings)`
        },
        {
          type: 'interactive_exercise',
          title: 'Parametric and Polar Curve Analyzer',
          component: 'InteractiveExercise',
          exerciseData: {
            instructions: `Create a comprehensive curve analysis system that can work with both parametric and polar equations.

Your system should:
1. Convert between parametric and polar representations when possible
2. Calculate key properties: arc length, area, tangent slopes
3. Identify special curve types (circle, ellipse, rose, cardioid, etc.)
4. Generate coordinate points for plotting

Implement these classes:
- ParametricCurve(x_func, y_func, t_range)
- PolarCurve(r_func, theta_range)  
- CurveAnalyzer() with conversion and analysis methods

Test with:
- Circle: x=cos(t), y=sin(t) 
- Cardioid: r=1+cos(θ)
- Calculate properties and identify curve types`,
            initialCode: `class ParametricCurve {
  constructor(xFunc, yFunc, tRange = [0, 2*Math.PI]) {
    this.x = xFunc;
    this.y = yFunc;
    this.tRange = tRange;
  }
  
  // Calculate dx/dt numerically
  dxdt(t, h = 0.0001) {
    return (this.x(t + h) - this.x(t - h)) / (2 * h);
  }
  
  // Calculate dy/dt numerically  
  dydt(t, h = 0.0001) {
    return (this.y(t + h) - this.y(t - h)) / (2 * h);
  }
  
  // Calculate dy/dx at parameter t
  slope(t) {
    // Implement slope calculation
  }
  
  // Calculate arc length numerically
  arcLength(steps = 1000) {
    // Implement arc length calculation
  }
}

class PolarCurve {
  constructor(rFunc, thetaRange = [0, 2*Math.PI]) {
    this.r = rFunc;
    this.thetaRange = thetaRange;
  }
  
  // Convert to parametric form
  toParametric() {
    // Return ParametricCurve equivalent
  }
  
  // Calculate area enclosed by curve
  area(steps = 1000) {
    // Implement area calculation using ∫(1/2)r²dθ
  }
}

class CurveAnalyzer {
  // Identify curve type based on equation form
  identifyCurve(curve) {
    // Analyze equation to determine curve type
    // Return: { type: string, properties: object }
  }
  
  // Convert parametric to polar (when possible)
  parametricToPolar(paramCurve) {
    // Attempt conversion to polar form
  }
  
  // Generate plot points
  generatePoints(curve, numPoints = 100) {
    // Return array of [x, y] coordinates
  }
}

// Test your implementation
const analyzer = new CurveAnalyzer();

// Test circle: x = cos(t), y = sin(t)
const circle = new ParametricCurve(
  t => Math.cos(t),
  t => Math.sin(t)
);

// Test cardioid: r = 1 + cos(θ)
const cardioid = new PolarCurve(
  theta => 1 + Math.cos(theta)
);

console.log("Circle analysis:", analyzer.identifyCurve(circle));
console.log("Circle arc length:", circle.arcLength());
console.log("Cardioid analysis:", analyzer.identifyCurve(cardioid));
console.log("Cardioid area:", cardioid.area());`,
            expectedOutput: `Circle analysis: { type: 'circle', properties: { radius: 1, center: [0, 0] } }
Circle arc length: 6.283185
Cardioid analysis: { type: 'cardioid', properties: { type: 'standard', orientation: 'horizontal' } }
Cardioid area: 4.712389`,
            hints: [
              'Use numerical differentiation for derivatives: f\'(x) ≈ [f(x+h) - f(x-h)]/(2h)',
              'Arc length: ∫√[(dx/dt)² + (dy/dt)²] dt using trapezoidal rule',
              'Polar area: (1/2)∫r² dθ using numerical integration',
              'Circle has constant distance from center: √(x² + y²) = constant',
              'Cardioid has form r = a(1 ± cos θ) or r = a(1 ± sin θ)',
              'Use pattern matching to identify curve types from equations'
            ]
          }
        }
      ],
      assessments: [
        {
          type: 'Parametric and Polar Applications Project',
          title: 'Curve Modeling and Analysis in Computer Graphics',
          points: 150,
          dueWeek: 10
        }
      ]
    },
    {
      id: 'improper-integrals-applications',
      title: 'Improper Integrals and Applications',
      duration: '3 weeks',
      learningObjectives: [
        'Evaluate improper integrals with infinite limits and discontinuous integrands',
        'Apply integration techniques to probability and statistics',
        'Solve differential equations using calculus methods',
        'Model real-world phenomena using advanced calculus concepts'
      ],
      content: [
        {
          type: 'lesson',
          title: 'Improper Integrals',
          content: `# Improper Integrals

## Definition

Improper integrals are integrals that have one or more of the following characteristics:
1. Infinite limits of integration
2. Integrands that become infinite at one or more points in the interval

## Type I: Infinite Limits

### Case 1: Upper limit is infinite
∫[a,∞) f(x) dx = lim(t→∞) ∫[a,t] f(x) dx

### Case 2: Lower limit is infinite  
∫(-∞,b] f(x) dx = lim(t→-∞) ∫[t,b] f(x) dx

### Case 3: Both limits are infinite
∫(-∞,∞) f(x) dx = ∫(-∞,c] f(x) dx + ∫[c,∞) f(x) dx

where c is any real number, and both integrals must converge.

## Type II: Discontinuous Integrands

### Case 1: Discontinuity at right endpoint
∫[a,b) f(x) dx = lim(t→b⁻) ∫[a,t] f(x) dx

### Case 2: Discontinuity at left endpoint
∫(a,b] f(x) dx = lim(t→a⁺) ∫[t,b] f(x) dx

### Case 3: Discontinuity at interior point c
∫[a,b] f(x) dx = ∫[a,c) f(x) dx + ∫(c,b] f(x) dx

## Examples

### Example 1: ∫[1,∞) (1/x²) dx
∫[1,∞) (1/x²) dx = lim(t→∞) ∫[1,t] x⁻² dx
= lim(t→∞) [-x⁻¹][1,t]
= lim(t→∞) [-1/t + 1]
= 0 + 1 = 1

This integral converges to 1.

### Example 2: ∫[1,∞) (1/x) dx
∫[1,∞) (1/x) dx = lim(t→∞) ∫[1,t] x⁻¹ dx
= lim(t→∞) [ln x][1,t]
= lim(t→∞) [ln t - ln 1]
= lim(t→∞) ln t = ∞

This integral diverges.

### Example 3: ∫[0,1] (1/√x) dx
∫[0,1] x⁻¹/² dx = lim(t→0⁺) ∫[t,1] x⁻¹/² dx
= lim(t→0⁺) [2√x][t,1]
= lim(t→0⁺) [2 - 2√t]
= 2 - 0 = 2

This integral converges to 2.

## Comparison Tests for Improper Integrals

### Direct Comparison Test
If 0 ≤ f(x) ≤ g(x) for x ≥ a:
- If ∫[a,∞) g(x) dx converges, then ∫[a,∞) f(x) dx converges
- If ∫[a,∞) f(x) dx diverges, then ∫[a,∞) g(x) dx diverges

### Limit Comparison Test  
If f(x) > 0 and g(x) > 0 for x ≥ a, and lim(x→∞) f(x)/g(x) = L > 0:
Then ∫[a,∞) f(x) dx and ∫[a,∞) g(x) dx have the same convergence behavior.

## p-Integral Test
∫[1,∞) (1/xᵖ) dx converges if and only if p > 1

## Programming Implementation

\`\`\`python
import numpy as np
from scipy import integrate
import matplotlib.pyplot as plt

class ImproperIntegral:
    def __init__(self, func, a=None, b=None, discontinuities=None):
        self.func = func
        self.a = a  # Lower limit (None for -∞)
        self.b = b  # Upper limit (None for +∞)
        self.discontinuities = discontinuities or []
    
    def evaluate(self, method='scipy'):
        """Evaluate the improper integral"""
        if method == 'scipy':
            return self._evaluate_scipy()
        else:
            return self._evaluate_numerical()
    
    def _evaluate_scipy(self):
        """Use scipy.integrate.quad for evaluation"""
        try:
            if self.a is None and self.b is None:
                # Both limits infinite
                result, error = integrate.quad(self.func, -np.inf, np.inf)
            elif self.a is None:
                # Lower limit infinite
                result, error = integrate.quad(self.func, -np.inf, self.b)
            elif self.b is None:
                # Upper limit infinite
                result, error = integrate.quad(self.func, self.a, np.inf)
            else:
                # Finite limits, check for discontinuities
                if self.discontinuities:
                    result, error = integrate.quad(
                        self.func, self.a, self.b, 
                        points=self.discontinuities
                    )
                else:
                    result, error = integrate.quad(self.func, self.a, self.b)
            
            return {
                'value': result,
                'error': error,
                'converges': not (np.isinf(result) or np.isnan(result))
            }
        except:
            return {
                'value': None,
                'error': None,
                'converges': False
            }
    
    def _evaluate_numerical(self, max_limit=1000, tolerance=1e-10):
        """Numerical evaluation using limits"""
        if self.b is None:  # Upper limit infinite
            values = []
            limits = np.logspace(1, 3, 20)  # Test increasing upper limits
            
            for upper_limit in limits:
                try:
                    val, _ = integrate.quad(self.func, self.a, upper_limit)
                    values.append(val)
                except:
                    return {'converges': False}
            
            # Check for convergence
            if len(values) >= 2:
                diff = abs(values[-1] - values[-2])
                if diff < tolerance:
                    return {
                        'value': values[-1],
                        'converges': True,
                        'convergence_test': f'Limit difference: {diff:.2e}'
                    }
            
            return {'converges': False}
    
    def comparison_test(self, comparison_func, test_type='direct'):
        """Apply comparison tests"""
        # This would implement comparison test logic
        pass

# Examples
def example_1():
    """∫[1,∞) 1/x² dx = 1"""
    def f(x):
        return 1/(x**2)
    
    integral = ImproperIntegral(f, 1, None)
    result = integral.evaluate()
    print(f"∫[1,∞) 1/x² dx = {result['value']:.6f}")
    print(f"Converges: {result['converges']}")

def example_2():
    """∫[0,1] 1/√x dx = 2"""
    def f(x):
        return 1/np.sqrt(x)
    
    integral = ImproperIntegral(f, 0, 1, discontinuities=[0])
    result = integral.evaluate()
    print(f"∫[0,1] 1/√x dx = {result['value']:.6f}")
    print(f"Converges: {result['converges']}")

example_1()
example_2()
\`\`\`

## Applications

Improper integrals appear in:
- Probability theory (normalizing probability density functions)
- Physics (electromagnetic field calculations)
- Engineering (signal analysis, control theory)
- Statistics (gamma and beta functions)
- Computer science (algorithmic analysis)`
        },
        {
          type: 'lesson',
          title: 'Applications in Probability and Statistics',
          content: `# Applications in Probability and Statistics

## Probability Density Functions

A probability density function (PDF) f(x) must satisfy:
1. f(x) ≥ 0 for all x
2. ∫(-∞,∞) f(x) dx = 1 (normalization condition)

## Common Probability Distributions

### Normal Distribution
f(x) = (1/(σ√(2π))) e^(-(x-μ)²/(2σ²))

where μ is the mean and σ is the standard deviation.

The normalization integral: ∫(-∞,∞) e^(-x²) dx = √π

### Exponential Distribution
f(x) = λe^(-λx) for x ≥ 0, λ > 0

Normalization: ∫[0,∞) λe^(-λx) dx = 1

### Gamma Distribution
f(x) = (β^α/Γ(α)) x^(α-1) e^(-βx) for x ≥ 0

where Γ(α) = ∫[0,∞) t^(α-1) e^(-t) dt is the gamma function.

## Expected Value and Variance

### Expected Value (Mean)
E[X] = ∫(-∞,∞) x f(x) dx

### Variance
Var(X) = E[X²] - (E[X])²
where E[X²] = ∫(-∞,∞) x² f(x) dx

### Moment Generating Function
M(t) = E[e^(tX)] = ∫(-∞,∞) e^(tx) f(x) dx

## Applications in Statistics

### Central Limit Theorem
As n approaches infinity, the distribution of sample means approaches a normal distribution, regardless of the original population distribution.

### Confidence Intervals
Using the normal distribution to construct confidence intervals:
P(μ - zα/2 · σ/√n ≤ X̄ ≤ μ + zα/2 · σ/√n) = 1 - α

## Programming Implementation

\`\`\`python
import numpy as np
import matplotlib.pyplot as plt
from scipy import integrate, stats

class ProbabilityDistribution:
    def __init__(self, pdf_func, support=(-np.inf, np.inf)):
        self.pdf = pdf_func
        self.support = support
    
    def normalize(self):
        """Check if PDF is properly normalized"""
        integral, _ = integrate.quad(self.pdf, *self.support)
        return abs(integral - 1.0) < 1e-10, integral
    
    def expected_value(self):
        """Calculate E[X]"""
        def x_pdf(x):
            return x * self.pdf(x)
        
        result, _ = integrate.quad(x_pdf, *self.support)
        return result
    
    def variance(self):
        """Calculate Var(X)"""
        mean = self.expected_value()
        
        def x2_pdf(x):
            return x**2 * self.pdf(x)
        
        second_moment, _ = integrate.quad(x2_pdf, *self.support)
        return second_moment - mean**2
    
    def cdf(self, x):
        """Calculate cumulative distribution function at x"""
        if x <= self.support[0]:
            return 0.0
        if x >= self.support[1]:
            return 1.0
        
        result, _ = integrate.quad(self.pdf, self.support[0], x)
        return result
    
    def plot(self, x_range=None, num_points=1000):
        """Plot the PDF"""
        if x_range is None:
            if self.support[0] == -np.inf:
                x_start = -5
            else:
                x_start = self.support[0]
            
            if self.support[1] == np.inf:
                x_end = 5
            else:
                x_end = self.support[1]
        else:
            x_start, x_end = x_range
        
        x = np.linspace(x_start, x_end, num_points)
        y = [self.pdf(xi) for xi in x]
        
        plt.figure(figsize=(10, 6))
        plt.plot(x, y, 'b-', linewidth=2)
        plt.xlabel('x')
        plt.ylabel('f(x)')
        plt.title('Probability Density Function')
        plt.grid(True)
        plt.show()

# Example: Exponential Distribution
def exponential_pdf(x, lam=1.0):
    if x < 0:
        return 0.0
    return lam * np.exp(-lam * x)

# Create distribution
exp_dist = ProbabilityDistribution(
    lambda x: exponential_pdf(x, 2.0),
    support=(0, np.inf)
)

# Analysis
is_normalized, integral_value = exp_dist.normalize()
print(f"Is normalized: {is_normalized}")
print(f"Integral value: {integral_value:.6f}")
print(f"Expected value: {exp_dist.expected_value():.6f}")
print(f"Variance: {exp_dist.variance():.6f}")
print(f"P(X ≤ 1): {exp_dist.cdf(1.0):.6f}")

# Example: Normal Distribution Implementation
class NormalDistribution(ProbabilityDistribution):
    def __init__(self, mu=0, sigma=1):
        self.mu = mu
        self.sigma = sigma
        
        def normal_pdf(x):
            return (1/(sigma * np.sqrt(2*np.pi))) * np.exp(-0.5*((x-mu)/sigma)**2)
        
        super().__init__(normal_pdf, (-np.inf, np.inf))
    
    def standard_score(self, x):
        """Calculate z-score"""
        return (x - self.mu) / self.sigma
    
    def confidence_interval(self, confidence_level=0.95):
        """Calculate confidence interval for the mean"""
        alpha = 1 - confidence_level
        z_critical = stats.norm.ppf(1 - alpha/2)
        margin = z_critical * self.sigma
        return (self.mu - margin, self.mu + margin)

# Example usage
normal = NormalDistribution(mu=10, sigma=2)
print(f"95% CI: {normal.confidence_interval()}")
\`\`\`

## Statistical Inference Applications

### Hypothesis Testing
Using improper integrals to calculate p-values and critical regions.

### Bayesian Statistics
Prior and posterior distributions often involve improper integrals in their normalization.

### Survival Analysis
Survival functions S(t) = P(T > t) = ∫[t,∞) f(u) du

## Applications in Machine Learning

### Maximum Likelihood Estimation
Finding parameters that maximize ∏ᵢ f(xᵢ; θ)

### Bayesian Inference
Computing posterior distributions using Bayes' theorem

### Information Theory
Entropy: H(X) = -∫ f(x) log f(x) dx`
        },
        {
          type: 'interactive_exercise',
          title: 'Probability Distribution Analyzer',
          component: 'InteractiveExercise',
          exerciseData: {
            instructions: `Build a comprehensive probability distribution analysis tool that can:

1. Verify that a given function is a valid probability density function
2. Calculate key statistical measures (mean, variance, standard deviation)
3. Compute probabilities and percentiles
4. Generate random samples from the distribution
5. Create visualizations of the PDF and CDF

Your analyzer should handle:
- Normal distribution: f(x) = (1/(σ√(2π))) * e^(-(x-μ)²/(2σ²))
- Exponential distribution: f(x) = λe^(-λx) for x ≥ 0
- Custom distributions defined by mathematical expressions

Implement methods:
- validatePDF(func, domain)
- calculateMoments(func, domain)
- computeProbability(func, domain, interval)
- generateSamples(func, domain, numSamples)

Test with exponential distribution λ=2 and normal distribution μ=0, σ=1`,
            initialCode: `class ProbabilityAnalyzer {
  constructor() {
    this.tolerance = 1e-10;
  }
  
  // Validate that a function is a proper PDF
  validatePDF(func, domain = [-Infinity, Infinity]) {
    // Check: ∫f(x)dx = 1 and f(x) ≥ 0
    // Return: { isValid: boolean, integral: number, issues: array }
  }
  
  // Calculate statistical moments
  calculateMoments(func, domain = [-Infinity, Infinity]) {
    // Compute mean, variance, standard deviation
    // Return: { mean: number, variance: number, stdDev: number }
  }
  
  // Compute probability P(a ≤ X ≤ b)
  computeProbability(func, domain, interval) {
    // Integrate func from interval[0] to interval[1]
    // Return: { probability: number, method: string }
  }
  
  // Find percentile (inverse CDF)
  findPercentile(func, domain, percentile) {
    // Find x such that P(X ≤ x) = percentile
    // Use numerical methods
  }
  
  // Generate random samples using inverse transform sampling
  generateSamples(func, domain, numSamples = 1000) {
    // Generate samples from the distribution
    // Return array of sample values
  }
  
  // Numerical integration helper
  integrate(func, a, b, steps = 10000) {
    // Implement trapezoidal rule or Simpson's rule
    // Handle infinite limits appropriately
  }
}

// Distribution definitions
const distributions = {
  exponential: (lambda) => (x) => x >= 0 ? lambda * Math.exp(-lambda * x) : 0,
  
  normal: (mu, sigma) => (x) => {
    const coefficient = 1 / (sigma * Math.sqrt(2 * Math.PI));
    const exponent = -0.5 * Math.pow((x - mu) / sigma, 2);
    return coefficient * Math.exp(exponent);
  }
};

// Test your implementation
const analyzer = new ProbabilityAnalyzer();

// Test exponential distribution with λ = 2
const expDist = distributions.exponential(2);
console.log("Exponential Distribution (λ=2):");
console.log("Validation:", analyzer.validatePDF(expDist, [0, Infinity]));
console.log("Moments:", analyzer.calculateMoments(expDist, [0, Infinity]));
console.log("P(0.5 ≤ X ≤ 1.5):", analyzer.computeProbability(expDist, [0, Infinity], [0.5, 1.5]));

// Test normal distribution with μ=0, σ=1
const normalDist = distributions.normal(0, 1);
console.log("\\nStandard Normal Distribution:");
console.log("Validation:", analyzer.validatePDF(normalDist));
console.log("Moments:", analyzer.calculateMoments(normalDist));
console.log("P(-1 ≤ X ≤ 1):", analyzer.computeProbability(normalDist, [-Infinity, Infinity], [-1, 1]));`,
            expectedOutput: `Exponential Distribution (λ=2):
Validation: { isValid: true, integral: 1.000000, issues: [] }
Moments: { mean: 0.500000, variance: 0.250000, stdDev: 0.500000 }
P(0.5 ≤ X ≤ 1.5): { probability: 0.233, method: 'numerical_integration' }

Standard Normal Distribution:
Validation: { isValid: true, integral: 1.000000, issues: [] }
Moments: { mean: 0.000000, variance: 1.000000, stdDev: 1.000000 }
P(-1 ≤ X ≤ 1): { probability: 0.683, method: 'numerical_integration' }`,
            hints: [
              'For infinite limits, use large finite values (e.g., ±100) for practical computation',
              'Exponential distribution: mean = 1/λ, variance = 1/λ²',
              'Standard normal: mean = 0, variance = 1, P(-1 ≤ X ≤ 1) ≈ 0.683',
              'Use trapezoidal rule: ∫f(x)dx ≈ (b-a)/(2n) * [f(a) + 2∑f(xi) + f(b)]',
              'For moment calculations: E[X] = ∫xf(x)dx, E[X²] = ∫x²f(x)dx',
              'Validate by checking integral equals 1 and function is non-negative'
            ]
          }
        }
      ],
      assessments: [
        {
          type: 'Applied Statistics Project',
          title: 'Real-World Data Analysis Using Calculus',
          points: 175,
          dueWeek: 13
        }
      ]
    },
    {
      id: 'multivariable-foundations',
      title: 'Introduction to Multivariable Calculus',
      duration: '2 weeks',
      learningObjectives: [
        'Understand functions of multiple variables and their domains',
        'Visualize multivariable functions using level curves and surfaces',
        'Calculate partial derivatives and understand their geometric meaning',
        'Apply the chain rule for multivariable functions'
      ],
      content: [
        {
          type: 'lesson',
          title: 'Functions of Multiple Variables',
          content: `# Functions of Multiple Variables

## Introduction

A function of multiple variables assigns a single output value to each point in its domain, which is a subset of n-dimensional space.

Examples:
- f(x, y) = x² + y² (function of two variables)
- g(x, y, z) = xyz + sin(x) (function of three variables)
- h(x₁, x₂, ..., xₙ) = Σᵢxᵢ² (function of n variables)

## Domain and Range

### Domain
The domain is the set of all input points for which the function is defined.

Examples:
- f(x, y) = √(x² + y²): Domain is all of ℝ²
- g(x, y) = 1/(x² + y² - 1): Domain is {(x,y) : x² + y² ≠ 1}
- h(x, y) = ln(x) + √(y): Domain is {(x,y) : x > 0, y ≥ 0}

### Range
The range is the set of all possible output values.

## Visualization Techniques

### Level Curves (2D functions)
For f(x, y) = c (constant), the level curve shows all points where the function has the same value.

Example: f(x, y) = x² + y²
- Level curves are circles: x² + y² = c
- c = 1: unit circle
- c = 4: circle of radius 2

### Level Surfaces (3D functions)
For f(x, y, z) = c, the level surface shows all points where the function equals c.

Example: f(x, y, z) = x² + y² + z²
- Level surfaces are spheres: x² + y² + z² = c

### 3D Graphs
For z = f(x, y), we can plot the surface in 3D space.

## Programming Implementation

\`\`\`python
import numpy as np
import matplotlib.pyplot as plt
from mpl_toolkits.mplot3d import Axes3D

class MultivariableFunction:
    def __init__(self, func, variables=2):
        self.func = func
        self.variables = variables
    
    def evaluate(self, *args):
        """Evaluate function at given point"""
        return self.func(*args)
    
    def plot_level_curves(self, x_range=(-5, 5), y_range=(-5, 5), 
                         levels=10, num_points=100):
        """Plot level curves for 2D function"""
        if self.variables != 2:
            raise ValueError("Level curves only for 2D functions")
        
        x = np.linspace(x_range[0], x_range[1], num_points)
        y = np.linspace(y_range[0], y_range[1], num_points)
        X, Y = np.meshgrid(x, y)
        
        Z = np.zeros_like(X)
        for i in range(X.shape[0]):
            for j in range(X.shape[1]):
                try:
                    Z[i, j] = self.func(X[i, j], Y[i, j])
                except:
                    Z[i, j] = np.nan
        
        plt.figure(figsize=(10, 8))
        contour = plt.contour(X, Y, Z, levels=levels)
        plt.clabel(contour, inline=True, fontsize=8)
        plt.xlabel('x')
        plt.ylabel('y')
        plt.title('Level Curves')
        plt.grid(True)
        plt.axis('equal')
        plt.show()
    
    def plot_3d_surface(self, x_range=(-5, 5), y_range=(-5, 5), 
                       num_points=50):
        """Plot 3D surface for 2D function"""
        if self.variables != 2:
            raise ValueError("3D surface only for 2D functions")
        
        x = np.linspace(x_range[0], x_range[1], num_points)
        y = np.linspace(y_range[0], y_range[1], num_points)
        X, Y = np.meshgrid(x, y)
        
        Z = np.zeros_like(X)
        for i in range(X.shape[0]):
            for j in range(X.shape[1]):
                try:
                    Z[i, j] = self.func(X[i, j], Y[i, j])
                except:
                    Z[i, j] = np.nan
        
        fig = plt.figure(figsize=(12, 9))
        ax = fig.add_subplot(111, projection='3d')
        
        surface = ax.plot_surface(X, Y, Z, cmap='viridis', alpha=0.8)
        ax.set_xlabel('x')
        ax.set_ylabel('y')
        ax.set_zlabel('z')
        ax.set_title('3D Surface')
        
        fig.colorbar(surface)
        plt.show()
    
    def find_domain_constraints(self, test_points=1000):
        """Attempt to identify domain constraints"""
        constraints = []
        
        if self.variables == 2:
            # Test random points
            for _ in range(test_points):
                x = np.random.uniform(-10, 10)
                y = np.random.uniform(-10, 10)
                try:
                    result = self.func(x, y)
                    if np.isnan(result) or np.isinf(result):
                        constraints.append(f"Issue at ({x:.2f}, {y:.2f})")
                except:
                    constraints.append(f"Undefined at ({x:.2f}, {y:.2f})")
        
        return constraints[:10]  # Return first 10 constraints found

# Examples
def example_paraboloid(x, y):
    """f(x,y) = x² + y²"""
    return x**2 + y**2

def example_saddle(x, y):
    """f(x,y) = x² - y²"""
    return x**2 - y**2

def example_restricted(x, y):
    """f(x,y) = ln(x² + y² - 1)"""
    if x**2 + y**2 <= 1:
        raise ValueError("Outside domain")
    return np.log(x**2 + y**2 - 1)

# Create function objects
paraboloid = MultivariableFunction(example_paraboloid)
saddle = MultivariableFunction(example_saddle)
restricted = MultivariableFunction(example_restricted)

# Analyze functions
print("Paraboloid at (1, 2):", paraboloid.evaluate(1, 2))
print("Saddle at (3, 1):", saddle.evaluate(3, 1))

# Find domain constraints
print("Restricted function constraints:")
print(restricted.find_domain_constraints())
\`\`\`

## Applications in Computer Science

### Machine Learning
- Cost functions: J(θ₁, θ₂, ..., θₙ) for parameter optimization
- Neural networks: activation functions with multiple inputs

### Computer Graphics
- Parametric surfaces: (x(u,v), y(u,v), z(u,v))
- Lighting models: intensity = f(normal, light_direction, view_direction)

### Data Science
- Regression analysis: y = f(x₁, x₂, ..., xₙ)
- Principal component analysis: dimensionality reduction

### Optimization
- Finding minimum/maximum of objective functions
- Constrained optimization problems`
        },
        {
          type: 'lesson',
          title: 'Partial Derivatives',
          content: `# Partial Derivatives

## Definition

For a function f(x, y), the partial derivatives are:

**Partial derivative with respect to x:**
∂f/∂x = lim(h→0) [f(x+h, y) - f(x, y)]/h

**Partial derivative with respect to y:**
∂f/∂y = lim(h→0) [f(x, y+h) - f(x, y)]/h

## Geometric Interpretation

- ∂f/∂x represents the slope of the surface in the x-direction
- ∂f/∂y represents the slope of the surface in the y-direction

## Notation

Multiple notations are used for partial derivatives:
- ∂f/∂x = fx = f₁
- ∂f/∂y = fy = f₂

## Computing Partial Derivatives

To compute ∂f/∂x, treat y as a constant and differentiate with respect to x.
To compute ∂f/∂y, treat x as a constant and differentiate with respect to y.

### Examples

#### Example 1: f(x, y) = x³y² + 2xy + y⁴
- ∂f/∂x = 3x²y² + 2y
- ∂f/∂y = 2x³y + 2x + 4y³

#### Example 2: f(x, y) = e^(xy) + ln(x²y)
- ∂f/∂x = ye^(xy) + 2/(xy) · y = ye^(xy) + 2/x
- ∂f/∂y = xe^(xy) + 1/(x²y) · x² = xe^(xy) + x/y

#### Example 3: f(x, y) = sin(x²y) + cos(xy²)
- ∂f/∂x = cos(x²y) · 2xy + (-sin(xy²)) · y² = 2xy cos(x²y) - y² sin(xy²)
- ∂f/∂y = cos(x²y) · x² + (-sin(xy²)) · 2xy = x² cos(x²y) - 2xy sin(xy²)

## Higher-Order Partial Derivatives

### Second-Order Partial Derivatives
- ∂²f/∂x² = fxx (pure second partial)
- ∂²f/∂y² = fyy (pure second partial)
- ∂²f/∂x∂y = fxy (mixed partial)
- ∂²f/∂y∂x = fyx (mixed partial)

### Clairaut's Theorem
If fxy and fyx are continuous, then fxy = fyx.

### Example: f(x, y) = x³y² + xy⁴
- fx = 3x²y² + y⁴
- fy = 2x³y + 4xy³
- fxx = 6xy²
- fyy = 2x³ + 12xy²
- fxy = 6x²y + 4y³
- fyx = 6x²y + 4y³ (Note: fxy = fyx)

## The Gradient Vector

The gradient of f(x, y) is:
∇f = (∂f/∂x, ∂f/∂y) = (fx, fy)

The gradient points in the direction of steepest increase.

## Programming Implementation

\`\`\`python
import numpy as np
import sympy as sp
from scipy.misc import derivative

class PartialDerivatives:
    def __init__(self, func, variables):
        self.func = func
        self.variables = variables
        self.symbolic_func = None
        self.symbolic_vars = None
    
    def numerical_partial(self, var_index, point, h=1e-8):
        """Compute partial derivative numerically"""
        def partial_func(t):
            point_copy = list(point)
            point_copy[var_index] = t
            return self.func(*point_copy)
        
        return derivative(partial_func, point[var_index], dx=h)
    
    def gradient_numerical(self, point, h=1e-8):
        """Compute gradient numerically"""
        grad = []
        for i in range(len(self.variables)):
            partial = self.numerical_partial(i, point, h)
            grad.append(partial)
        return np.array(grad)
    
    def symbolic_setup(self, func_expression):
        """Set up symbolic computation"""
        self.symbolic_vars = sp.symbols(self.variables)
        self.symbolic_func = func_expression
    
    def symbolic_partial(self, var_name):
        """Compute partial derivative symbolically"""
        if self.symbolic_func is None:
            raise ValueError("Must call symbolic_setup first")
        
        var = sp.Symbol(var_name)
        return sp.diff(self.symbolic_func, var)
    
    def symbolic_gradient(self):
        """Compute symbolic gradient"""
        if self.symbolic_func is None:
            raise ValueError("Must call symbolic_setup first")
        
        grad = []
        for var in self.symbolic_vars:
            partial = sp.diff(self.symbolic_func, var)
            grad.append(partial)
        return grad
    
    def hessian_matrix(self):
        """Compute Hessian matrix (matrix of second partial derivatives)"""
        if self.symbolic_func is None:
            raise ValueError("Must call symbolic_setup first")
        
        n = len(self.symbolic_vars)
        hessian = sp.zeros(n, n)
        
        for i in range(n):
            for j in range(n):
                hessian[i, j] = sp.diff(self.symbolic_func, 
                                      self.symbolic_vars[i], 
                                      self.symbolic_vars[j])
        return hessian

# Example usage
def example_function(x, y):
    return x**3 * y**2 + 2*x*y + y**4

# Numerical approach
pd_num = PartialDerivatives(example_function, ['x', 'y'])
point = [2, 1]
print(f"∂f/∂x at (2,1): {pd_num.numerical_partial(0, point):.6f}")
print(f"∂f/∂y at (2,1): {pd_num.numerical_partial(1, point):.6f}")
print(f"Gradient at (2,1): {pd_num.gradient_numerical(point)}")

# Symbolic approach
x, y = sp.symbols('x y')
func_expr = x**3 * y**2 + 2*x*y + y**4

pd_sym = PartialDerivatives(None, ['x', 'y'])
pd_sym.symbolic_setup(func_expr)

print(f"\\nSymbolic ∂f/∂x: {pd_sym.symbolic_partial('x')}")
print(f"Symbolic ∂f/∂y: {pd_sym.symbolic_partial('y')}")
print(f"Symbolic gradient: {pd_sym.symbolic_gradient()}")
print(f"Hessian matrix:")
print(pd_sym.hessian_matrix())
\`\`\`

## Applications

### Optimization
Critical points occur where ∇f = 0 (all partial derivatives equal zero).

### Physics and Engineering
- Heat equation: ∂u/∂t = α(∂²u/∂x² + ∂²u/∂y²)
- Wave equation: ∂²u/∂t² = c²(∂²u/∂x² + ∂²u/∂y²)

### Economics
- Marginal cost, marginal revenue functions
- Utility maximization with budget constraints

### Machine Learning
- Gradient descent: θ = θ - α∇J(θ)
- Backpropagation in neural networks`
        },
        {
          type: 'interactive_exercise',
          title: 'Multivariable Function Explorer',
          component: 'InteractiveExercise',
          exerciseData: {
            instructions: `Create a comprehensive multivariable function analysis tool that can:

1. Parse mathematical expressions of multivariable functions
2. Compute partial derivatives symbolically and numerically
3. Find critical points (where gradient = 0)
4. Classify critical points using the second derivative test
5. Generate level curve data for visualization

Your tool should handle functions like:
- f(x,y) = x² + y² (paraboloid)
- f(x,y) = x² - y² (saddle)
- f(x,y) = sin(x) * cos(y) (wave function)

Implement:
- parseFunction(expression) - convert string to function
- computePartials(func, variables) - find ∂f/∂x, ∂f/∂y
- findCriticalPoints(func) - solve ∇f = 0
- classifyPoint(func, point) - local min/max/saddle
- generateLevelCurves(func, levels) - data for plotting

Test with f(x,y) = x² + y² - 2x - 4y + 5`,
            initialCode: `class MultivariableFunctionExplorer {
  constructor() {
    this.tolerance = 1e-8;
  }
  
  // Parse mathematical expression into evaluable function
  parseFunction(expression) {
    // Convert string like "x^2 + y^2" into function
    // Return: function that takes (x, y) and returns value
    // Handle common math functions: sin, cos, exp, log, sqrt, ^
  }
  
  // Compute partial derivatives numerically
  computePartials(func, point, h = 1e-8) {
    // Calculate ∂f/∂x and ∂f/∂y at given point
    // Use: [f(x+h,y) - f(x-h,y)] / (2h)
    // Return: { fx: number, fy: number }
  }
  
  // Compute second partial derivatives for classification
  computeSecondPartials(func, point, h = 1e-8) {
    // Calculate fxx, fyy, fxy for second derivative test
    // Return: { fxx: number, fyy: number, fxy: number }
  }
  
  // Find critical points where gradient = 0
  findCriticalPoints(func, searchRegion = [[-5,5], [-5,5]], gridSize = 50) {
    // Use numerical methods to find where fx ≈ 0 and fy ≈ 0
    // Return: array of critical points [x, y]
  }
  
  // Classify critical point using second derivative test
  classifyPoint(func, point) {
    // D = fxx*fyy - (fxy)²
    // If D > 0 and fxx > 0: local minimum
    // If D > 0 and fxx < 0: local maximum  
    // If D < 0: saddle point
    // If D = 0: inconclusive
    // Return: { type: string, discriminant: number }
  }
  
  // Generate level curve data
  generateLevelCurves(func, levels, xRange = [-5,5], yRange = [-5,5], resolution = 100) {
    // Create grid and evaluate function
    // Return: { x: array, y: array, z: 2D array, levels: array }
  }
  
  // Complete analysis of a function
  analyze(expression) {
    // Parse function, find critical points, classify them
    // Return comprehensive analysis object
  }
}

// Math utilities for expression parsing
const MathUtils = {
  // Replace common math notation
  preprocessExpression(expr) {
    return expr
      .replace(/\\^/g, '**')  // Convert ^ to **
      .replace(/sin/g, 'Math.sin')
      .replace(/cos/g, 'Math.cos')
      .replace(/exp/g, 'Math.exp')
      .replace(/log/g, 'Math.log');
  },
  
  // Safe evaluation of mathematical expressions
  evaluateExpression(expr, x, y) {
    // Create safe evaluation context
    const context = { x, y, Math };
    try {
      return Function('x', 'y', 'Math', \`return \${expr}\`)(x, y, Math);
    } catch (e) {
      return NaN;
    }
  }
};

// Test your implementation
const explorer = new MultivariableFunctionExplorer();

// Test function: f(x,y) = x² + y² - 2x - 4y + 5
const testExpression = "x**2 + y**2 - 2*x - 4*y + 5";
console.log("Analyzing f(x,y) = x² + y² - 2x - 4y + 5");

const analysis = explorer.analyze(testExpression);
console.log("Analysis results:", analysis);

// Expected: Critical point at (1, 2), local minimum, value = 0`,
            expectedOutput: `Analyzing f(x,y) = x² + y² - 2x - 4y + 5
Analysis results: {
  expression: "x**2 + y**2 - 2*x - 4*y + 5",
  criticalPoints: [[1, 2]],
  classifications: [
    { point: [1, 2], type: "local minimum", discriminant: 4, value: 0 }
  ],
  levelCurves: { x: [...], y: [...], z: [[...]], levels: [0, 1, 4, 9, 16] }
}`,
            hints: [
              'Use Function constructor to create functions from strings: new Function("x", "y", "return " + expression)',
              'Numerical partial derivatives: fx ≈ [f(x+h,y) - f(x-h,y)]/(2h)',
              'For critical points, search grid and find where |∇f| < tolerance',
              'Second derivative test: D = fxx*fyy - fxy², check sign of D and fxx',
              'The test function has minimum at (1,2) with value 0',
              'Handle edge cases: undefined values, division by zero, invalid expressions'
            ]
          }
        }
      ],
      assessments: [
        {
          type: 'Multivariable Calculus Foundation Project',
          title: 'Advanced Function Analysis and Optimization',
          points: 125,
          dueWeek: 15
        }
      ]
    }
  ],
  assessments: [
    {
      id: 'midterm',
      title: 'Midterm Examination',
      type: 'exam',
      date: 'Week 8',
      duration: '150 minutes',
      points: 250,
      description: 'Comprehensive exam covering integration techniques, sequences and series, and parametric/polar coordinate systems.'
    },
    {
      id: 'final',
      title: 'Final Examination',
      type: 'exam',
      date: 'Week 15',
      duration: '180 minutes',
      points: 300,
      description: 'Cumulative exam covering all Calculus II topics including improper integrals, applications, and multivariable foundations.'
    },
    {
      id: 'comprehensive-project',
      title: 'Applied Calculus II Research Project',
      type: 'project',
      date: 'Week 15',
      points: 200,
      description: 'Independent research project applying Calculus II concepts to a real-world problem in computer science, engineering, or data science.'
    }
  ],
  resources: [
    {
      category: 'Textbooks',
      items: [
        'Calculus: Early Transcendentals by James Stewart',
        'Calculus by Michael Spivak',
        'Advanced Calculus by Patrick Fitzpatrick',
        'Calculus with Applications by Margaret Lial'
      ]
    },
    {
      category: 'Online Resources',
      items: [
        'Khan Academy Calculus II',
        'MIT OpenCourseWare 18.02 Multivariable Calculus',
        'Paul\'s Online Math Notes - Calculus II',
        'Wolfram Alpha for verification and visualization',
        'Desmos Graphing Calculator for multivariable functions'
      ]
    },
    {
      category: 'Mathematical Software',
      items: [
        'MATLAB/Octave for numerical computation',
        'Python with NumPy, SciPy, and Matplotlib',
        'Mathematica for symbolic computation',
        'GeoGebra for geometric visualization',
        'R for statistical applications'
      ]
    },
    {
      category: 'Programming Tools',
      items: [
        'Jupyter Notebooks for interactive computing',
        'VS Code with math extensions',
        'LaTeX for mathematical typesetting',
        'Git for version control of projects',
        'GitHub for collaboration and portfolio'
      ]
    },
    {
      category: 'Reference Materials',
      items: [
        'Integration Techniques Quick Reference',
        'Series Convergence Tests Summary',
        'Polar and Parametric Formulas Sheet',
        'Multivariable Calculus Notation Guide',
        'Statistical Distribution Properties Table'
      ]
    }
  ],
  gradingScale: {
    'A': '93-100%',
    'A-': '90-92%',
    'B+': '87-89%',
    'B': '83-86%',
    'B-': '80-82%',
    'C+': '77-79%',
    'C': '73-76%',
    'C-': '70-72%',
    'D': '60-69%',
    'F': 'Below 60%'
  },
  gradingBreakdown: {
    'Module Assessments': '30%',
    'Midterm Exam': '25%',
    'Final Exam': '30%',
    'Research Project': '15%'
  },
  attendancePolicy: 'Regular attendance is essential for mastering advanced calculus concepts. Students are expected to participate in problem-solving sessions, group work, and mathematical discussions.',
  latePolicy: 'Mathematical assignments submitted late will be penalized 10% per day. Extensions may be granted for complex computational projects with proper documentation of technical difficulties.',
  accommodations: 'Students with disabilities requiring accommodations should contact the Office of Disability Services. Common accommodations include extended time for computational work, alternative formats for visual materials, and assistive technology for mathematical notation.',
  academicIntegrity: 'All work must demonstrate original understanding and application of mathematical concepts. While collaboration on concept understanding is encouraged, individual problem solutions must be original. Proper citation required for any external mathematical resources or computational tools.',
  technologyRequirements: 'Students need access to a computer capable of running mathematical software, graphing utilities, and computational environments. Reliable internet connection required for accessing online mathematical resources and submitting digital assignments.',
  softwareRequirements: [
    'Mathematical software (MATLAB, Python, or Mathematica)',
    'Graphing calculator or equivalent software',
    'LaTeX editor for mathematical typesetting',
    'Spreadsheet software for data analysis',
    'Web browser with modern JavaScript support',
    'PDF reader for mathematical texts and assignments'
  ],
  courseEvaluation: 'Student feedback collected through mathematical concept surveys, computational project reviews, and comprehensive course evaluations to ensure theoretical rigor and practical applicability of advanced calculus skills.'
};